import MusicTrack from "@/domain/entities/music/MusicTrack";
import musicTrackMapper from "@/mappers/music/musicTrackMapper";
import { useMusics } from "@cs_teamwaydev/mailvista-sdk_react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "./utils/useToast";

const FADE_DURATION = 1000; // Duration of fade in/out in milliseconds
const VOLUME_STEP = 0.05; // Volume change per interval

const useMusicManager = () => {
    const musicLibrary = useMusics();
    const { toast } = useToast();
    const { t } = useTranslation();
    const [currentTrack, setCurrentTrack] = useState<MusicTrack | null>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [playlist, setPlaylist] = useState<MusicTrack[]>([]);
    const fadeIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const audio = useMemo(() => {
        const newAudio = new Audio();
        newAudio.volume = 0;
        newAudio.src = currentTrack?.url || "";

        return newAudio;
    }, []);

    const fadeVolume = (
        startVolume: number,
        endVolume: number,
        onComplete?: () => void,
    ) => {
        if (fadeIntervalRef.current) {
            clearInterval(fadeIntervalRef.current);
        }

        if (!audio) return;

        const steps = Math.abs(endVolume - startVolume) / VOLUME_STEP;
        const stepDuration = FADE_DURATION / steps;
        let currentStep = 0;

        fadeIntervalRef.current = setInterval(() => {
            if (!audio) {
                clearInterval(fadeIntervalRef.current!);

                return;
            }

            currentStep++;
            const progress = currentStep / steps;
            const newVolume =
                startVolume + (endVolume - startVolume) * progress;
            audio.volume = Math.max(0, Math.min(1, newVolume));

            if (currentStep >= steps) {
                clearInterval(fadeIntervalRef.current!);
                onComplete?.();
            }
        }, stepDuration);
    };

    const changeTrack = (newTrack: MusicTrack) => {
        if (!audio) return;
        const previousTrack = audio.src;
        const nextTrack = newTrack.url;

        if (previousTrack && previousTrack !== nextTrack) {
            fadeVolume(1, 0, () => {
                if (audio) {
                    audio.src = nextTrack;
                    audio.volume = 0;
                    (async () => {
                        try {
                            await audio.play();
                            fadeVolume(0, 1);
                        } catch (error) {
                            console.error("Failed to play audio:", error);
                            setIsPlaying(false);
                        }
                    })();
                    setIsPlaying(true);
                }
            });
        } else {
            audio.src = nextTrack;
            audio.volume = 0;
            (async () => {
                try {
                    await audio.play();
                    fadeVolume(0, 1);
                } catch (error) {
                    console.error("Failed to play audio:", error);
                    setIsPlaying(false);
                }
            })();
            setIsPlaying(true);
        }
    };

    const getAllMusics = (): MusicTrack[] => {
        const tracks = musicLibrary.getAllMusics();

        return tracks.map(musicTrackMapper);
    };

    const initializePlaylist = () => {
        try {
            const allTracks = getAllMusics();
            // Shuffle the playlist
            const shuffledTracks = [...allTracks].sort(
                () => Math.random() - 0.5,
            );
            setPlaylist(shuffledTracks);

            return shuffledTracks[0];
        } catch (error) {
            console.error("Failed to initialize playlist", error);
            toast({
                title: t("errors.music"),
                description: t("errors.global.description"),
                variant: "error",
            });

            return null;
        }
    };

    const play = async () => {
        if (!currentTrack) {
            const firstTrack = initializePlaylist();
            if (firstTrack) {
                setCurrentTrack(firstTrack);
                setIsPlaying(true);
                changeTrack(firstTrack);
            }
        } else {
            setIsPlaying(true);
            if (audio) {
                audio.volume = 0;
                audio
                    .play()
                    .then(() => fadeVolume(0, 1))
                    .catch((error) => {
                        console.error("Failed to play audio:", error);
                        setIsPlaying(false);
                    });
            }
        }
    };

    const pause = () => {
        if (audio) {
            setIsPlaying(false);
            fadeVolume(1, 0, () => {
                audio?.pause();
            });
        } else {
            setIsPlaying(false);
        }
    };

    const next = () => {
        if (playlist.length === 0) {
            const firstTrack = initializePlaylist();
            if (firstTrack) {
                setCurrentTrack(firstTrack);
                changeTrack(firstTrack);
            }

            return;
        }
        const currentIdx = playlist.findIndex(
            (track) => track.url === currentTrack?.url,
        );
        const nextIndex =
            currentIdx === -1 ? 0 : (currentIdx + 1) % playlist.length;
        const nextTrack = playlist[nextIndex];
        setCurrentTrack(nextTrack);
        changeTrack(nextTrack);
    };

    const previous = () => {
        if (playlist.length === 0) {
            const firstTrack = initializePlaylist();
            if (firstTrack) {
                setCurrentTrack(firstTrack);
                changeTrack(firstTrack);
            }

            return;
        }
        const currentIdx = playlist.findIndex(
            (track) => track.url === currentTrack?.url,
        );
        const prevIndex =
            currentIdx === -1
                ? 0
                : (currentIdx - 1 + playlist.length) % playlist.length;
        const prevTrack = playlist[prevIndex];
        setCurrentTrack(prevTrack);
        setIsPlaying(true);
        changeTrack(prevTrack);
    };

    useEffect(() => {
        if (!audio) return;

        const handleTrackEnd = () => {
            next();
        };

        audio.addEventListener("ended", handleTrackEnd);

        return () => {
            audio.removeEventListener("ended", handleTrackEnd);
        };
    }, [audio, next]);

    return {
        getAllMusics,
        currentTrack,
        isPlaying,
        play,
        pause,
        next,
        previous,
    };
};

export default useMusicManager;
